import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useApi } from '../../hooks/useApi';
import {
  FILES_NOTE, POLICY_URL, deleteUrl, devicesUrl, errorText, filterText, machinesMatching, policyUrl, rulesEditable, ruleSetBody as newRuleSetBody,
} from '../../pages/SoftwarePolicy/policyEdit';
import { definitionBody, existingRule, policyRows, removeRuleBody, ruleSetBody } from '../../pages/SoftwareBuilder/builderLogic';
import { useV2Account } from '../useV2Account';
import AddProfileDialog from './AddProfileDialog';
import AddSoftwareBlade from './AddSoftwareBlade';
import PolicyValue from './PolicyValue';
import { SortHeader, useSort } from './sortable';

const list = (v) => (Array.isArray(v) ? v : []);

// Software policy, V2: the profiles (sets of labels) of an account. A profile opens to its software, and each line's In policy value is
// changed in place; "Add software" opens a blade to find more. Everything is saved as it is changed.
export default function PolicyPage() {
  const jApi = useApi();
  const apiRef = useRef(jApi);
  apiRef.current = jApi;
  const { accounts, account, choose, error: accountsError } = useV2Account();
  const [policy, setPolicy] = useState({ state: 'idle' });
  const [devices, setDevices] = useState({ state: 'idle' });
  const [open, setOpen] = useState(null);                 // the profile that is open: the others are hidden
  const [bladeFor, setBladeFor] = useState(null);
  const [busyKey, setBusyKey] = useState(null);
  const [adding, setAdding] = useState(false);
  const software = useSort();
  const [deleting, setDeleting] = useState(null);
  const [notice, setNotice] = useState('');
  const [problem, setProblem] = useState('');

  const code = account?.code;
  const editable = policy.state === 'ok' && rulesEditable(policy);
  const ruleSets = [...list(policy.ruleSets)].sort((a, b) => list(a.filter).length - list(b.filter).length || filterText(a.filter).localeCompare(filterText(b.filter)));
  const current = (id) => list(policy.ruleSets).find((rs) => rs.id === id);

  const load = useCallback(async () => {
    if (!code) return;
    setPolicy({ state: 'loading' });
    setDevices({ state: 'loading' });
    const reads = [
      apiRef.current.request('GET', policyUrl(code)).then((r) => setPolicy({ state: 'ok', ...r.data }))
        .catch((ex) => setPolicy({ state: 'error', error: errorText(ex) })),
      apiRef.current.request('GET', devicesUrl(code)).then((r) => setDevices({ state: 'ok', ...r.data }))
        .catch((ex) => setDevices({ state: 'error', error: errorText(ex), devices: [] })),
    ];
    await Promise.all(reads);
  }, [code]);

  useEffect(() => { setOpen(null); setBladeFor(null); setNotice(''); setProblem(''); load(); }, [load]);

  // Set what a profile says about a program ('none' takes it out). A program with no definition gets one first. The saved profile replaces
  // the one on the page, so the line shows the new value at once; a failure leaves it as it was and says why.
  const setMode = async (ruleSet, item, mode) => {
    setBusyKey(item.key);
    setProblem('');
    setNotice('');
    try {
      let created = null;
      let shownAs = null;                                  // what the page knows the program as until it is read again (nothing is stored for it)
      let body;
      if (mode === 'none') {
        body = removeRuleBody(code, ruleSet, item.key);
      } else {
        const known = list(policy.definitions).some((d) => d.key === item.key);
        // A winget program needs no stored definition: its winget id is the key and the API works the rest out from the catalog.
        if (!item.definitionExists && !known && !item.wingetId) {
          created = definitionBody(code, { ...item, mode });
          await apiRef.current.request('POST', POLICY_URL, created);
        }
        if (!created && !known && item.wingetId) shownAs = { key: item.key, displayName: item.name, note: '', detect: { namePattern: null, wingetId: item.wingetId }, actions: {}, builtIn: true };
        body = ruleSetBody(code, ruleSet, { ...item, mode, reason: existingRule(ruleSet, item.key)?.reason || '' });
      }
      const response = await apiRef.current.request('POST', POLICY_URL, body);
      const saved = Array.isArray(response?.data?.rules) ? response.data : body;
      setPolicy((p) => ({
        ...p,
        definitions: created || shownAs ? [...list(p.definitions), created || shownAs] : p.definitions,
        ruleSets: list(p.ruleSets).map((rs) => (rs.id === ruleSet.id ? { ...rs, ...saved, id: rs.id } : rs)),
      }));
      setNotice(`${item.name}: ${mode === 'none' ? 'not in the profile any more' : mode}.`);
    } catch (ex) {
      setProblem(`${item.name}: ${errorText(ex)}`);
    } finally {
      setBusyKey(null);
    }
  };

  const addProfile = async (filter) => {
    const response = await apiRef.current.request('POST', POLICY_URL, newRuleSetBody(code, { description: '', enabled: true, filter, rules: [] }));
    setAdding(false);
    await load();
    if (response?.data?.id) setOpen(response.data.id);
    setNotice('Profile added. Add its software with "Add software".');
  };

  const removeProfile = async () => {
    const target = deleting;
    try {
      await apiRef.current.request('DELETE', deleteUrl(code, target.id));
      setDeleting(null);
      if (open === target.id) setOpen(null);
      setNotice('Profile deleted.');
      await load();
    } catch (ex) {
      setDeleting(null);
      setProblem(errorText(ex));
    }
  };

  const toggle = (id) => setOpen(open === id ? null : id);
  const lockedReason = FILES_NOTE;
  const shown = ruleSets.filter((rs) => !open || open === rs.id);
  // The newest version the winget catalog has, by the program's key (the API puts it on each definition that names a winget package).
  const latestOf = Object.fromEntries(list(policy.definitions).map((d) => [d.key, d.latest]));

  return (
    <div>
      <div className="v2-head">
        <div>
          <h1>Software policy</h1>
          <p>Which software the machines of an account must have, may have, or must not have. Choose a profile to see its software.</p>
        </div>
        <div>
          <select className="v2-select v2-account" aria-label="Account" value={code || ''} onChange={(e) => choose(e.target.value)}>
            {accounts.map((a) => <option key={a.code} value={a.code}>{a.name ? `${a.code} - ${a.name}` : a.code}</option>)}
          </select>
        </div>
      </div>

      {accountsError && <div className="v2-alert err">{accountsError}</div>}
      {policy.state === 'ok' && !editable && <div className="v2-alert info">{FILES_NOTE}</div>}
      {policy.state === 'error' && <div className="v2-alert err">{policy.error}</div>}
      {problem && <div className="v2-alert err" role="alert">{problem}</div>}
      {notice && <div className="v2-alert" role="status">{notice}</div>}

      <section className="v2-card">
        <div className="v2-bar">
          <p>A profile is a set of labels. Its machines get the software listed under it. More labels means more specific.</p>
          <button type="button" className="v2-textbtn" disabled={!editable} title={editable ? '' : lockedReason} onClick={() => setAdding(true)}>+ Add profile</button>
        </div>
        {policy.state === 'loading' && <div className="v2-loading"><span className="v2-spin" role="status" aria-label="Loading" /></div>}
        {policy.state === 'ok' && (ruleSets.length === 0 ? <div className="v2-empty">This account has no profiles yet.</div> : (
          <table className="v2-table" aria-label="Profiles">
            <thead><tr><th style={{ width: 30 }} /><th>Profile</th><th>Machines</th><th style={{ width: 40 }} /></tr></thead>
            <tbody>
              {shown.map((rs) => {
                const isOpen = open === rs.id;
                const matched = machinesMatching(rs.filter, devices.devices);
                const rows = policyRows(rs, policy.definitions, [], 0);
                return (
                  <React.Fragment key={rs.id}>
                    <tr className="row" aria-expanded={isOpen} tabIndex={0} onClick={() => toggle(rs.id)}
                      onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) { e.preventDefault(); toggle(rs.id); } }}>
                      <td><span className="v2-caret" aria-hidden="true">&#9656;</span></td>
                      <td>{list(rs.filter).map((f) => <span className="v2-chip" key={`${f.label}-${f.value}`}>{`${f.label} is ${f.value}`}</span>)}</td>
                      <td>{devices.state === 'ok' ? matched.length : '-'}</td>
                      <td>
                        <button type="button" className="v2-del" aria-label={`Delete profile ${filterText(rs.filter)}`} disabled={!editable} title={editable ? 'Delete' : lockedReason}
                          onClick={(e) => { e.stopPropagation(); setDeleting(rs); }}>&#128465;</button>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="detail">
                        <td colSpan={4}>
                          <div className="v2-detail">
                            <div className="v2-detail-head">
                              <h3>Software in this profile</h3>
                              <button type="button" className="v2-textbtn" disabled={!editable} title={editable ? '' : lockedReason} onClick={() => setBladeFor(rs.id)}>+ Add software</button>
                            </div>
                            {rows.length === 0 ? <div className="v2-empty">No software in this profile yet.</div> : (
                              <table className="v2-table" aria-label="Software in this profile">
                                <thead>
                                  <tr>
                                    <SortHeader k="software" sort={software.sort} onSort={software.toggle}>Software</SortHeader>
                                    <SortHeader k="policy" sort={software.sort} onSort={software.toggle}>Policy</SortHeader>
                                    <SortHeader k="latest" sort={software.sort} onSort={software.toggle}>Latest</SortHeader>
                                  </tr>
                                </thead>
                                <tbody>
                                  {software.apply(rows, {
                                    software: { get: (r) => r.name },
                                    policy: { get: (r) => r.mode || 'none' },
                                    latest: { get: (r) => latestOf[r.key] },
                                  }).map((r) => (
                                    <tr key={r.key}>
                                      <td><div className="name" data-testid="profile-software-name">{r.name}</div><div className="v2-sub">{r.key}</div></td>
                                      <td>
                                        <PolicyValue name={r.name} value={r.mode} busy={busyKey === r.key} disabled={!editable || busyKey !== null} title={lockedReason}
                                          onChange={(value) => setMode(rs, { key: r.key, name: r.name, definitionExists: true }, value)} />
                                      </td>
                                      <td data-testid="latest-version">{latestOf[r.key] || '\u2014'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                            <div className="v2-hint">Click a value to change it.</div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        ))}
      </section>

      {bladeFor && current(bladeFor) && (
        <AddSoftwareBlade account={code} ruleSet={current(bladeFor)} editable={editable} lockedReason={lockedReason} busyKey={busyKey}
          onSet={(item, mode) => setMode(current(bladeFor), item, mode)} onClose={() => setBladeFor(null)} />
      )}
      {adding && <AddProfileDialog account={code} devices={devices.devices} onSave={addProfile} onClose={() => setAdding(false)} />}
      {deleting && (
        <div className="v2-dialog-host">
          <div className="v2-dialog" role="dialog" aria-label="Delete profile">
            <h2>Delete this profile?</h2>
            <p>{`Delete the profile for ${filterText(deleting.filter)} and its ${list(deleting.rules).length} ${list(deleting.rules).length === 1 ? 'item' : 'items'}? The machines it matched get the software of the other profiles. The change is kept in the history.`}</p>
            <div className="actions">
              <button type="button" className="v2-btn" onClick={() => setDeleting(null)}>Cancel</button>
              <button type="button" className="v2-btn primary" onClick={removeProfile}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
